import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";

const StoreUserKeysSchema = z.object({
  publicKey: z.string(),
  encryptedPrivateKey: z.string(),
  recoveryCodeHashes: z.array(z.string()),
  recoveryEncryptedPrivateKeys: z.array(z.string()).optional(),
  recoverySalt: z.string().optional(),
  salt: z.string(),
});

const VerifyRecoveryCodesSchema = z.object({
  codeHashes: z.array(z.string()).length(2),
});

const ResetPinSchema = z.object({
  encryptedPrivateKey: z.string(),
  salt: z.string(),
  recoveryCodeHashes: z.array(z.string()).optional(),
});

/** Store encryption keys after PIN setup. */
export const storeUserKeysFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(StoreUserKeysSchema)
  .handler(async ({ data, context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { userEncryptionKeys } = await import("drizzle/schema");

    const db = await getDBWithInit();
    const userId = context.user.id;

    const [row] = await db
      .insert(userEncryptionKeys)
      .values({
        userId,
        publicKey: data.publicKey,
        encryptedPrivateKey: data.encryptedPrivateKey,
        recoveryCodeHashes: data.recoveryCodeHashes,
        recoveryEncryptedPrivateKeys: data.recoveryEncryptedPrivateKeys,
        recoverySalt: data.recoverySalt,
        salt: data.salt,
      })
      .onConflictDoUpdate({
        target: userEncryptionKeys.userId,
        set: {
          publicKey: data.publicKey,
          encryptedPrivateKey: data.encryptedPrivateKey,
          recoveryCodeHashes: data.recoveryCodeHashes,
          recoveryEncryptedPrivateKeys: data.recoveryEncryptedPrivateKeys,
          recoverySalt: data.recoverySalt,
          salt: data.salt,
          failedPinAttempts: 0,
          lockedUntil: null,
        },
      })
      .returning({ id: userEncryptionKeys.id });

    return { id: row!.id };
  });

/** Fetch user's encryption key record. Returns null if not set up. */
export const getUserEncryptionKeysFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const db = await getDBWithInit();
    const userId = context.user.id;

    const row = await db.query.userEncryptionKeys.findFirst({
      where: { userId },
      columns: {
        publicKey: true,
        encryptedPrivateKey: true,
        salt: true,
        failedPinAttempts: true,
        lockedUntil: true,
      },
    });

    if (!row) return null;
    if (row.lockedUntil && row.lockedUntil > new Date()) {
      return { ...row, encryptedPrivateKey: null };
    }
    return row;
  });

/** Fetch any user's public key by userId (for sharing). */
export const getPublicKeyByUserIdFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ userId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const db = await getDBWithInit();

    const row = await db.query.userEncryptionKeys.findFirst({
      where: { userId: data.userId },
      columns: { publicKey: true },
    });

    return row?.publicKey ?? null;
  });

/** Verify 2 recovery code hashes against stored hashes. Returns matching recovery-wrapped key if available. */
export const verifyRecoveryCodesFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(VerifyRecoveryCodesSchema)
  .handler(async ({ data, context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { userEncryptionKeys } = await import("drizzle/schema");
    const { eq } = await import("drizzle-orm");
    const { MAX_ATTEMPTS, LOCKOUT_MINUTES } =
      await import("~/lib/crypto/constants");

    const db = await getDBWithInit();
    const userId = context.user.id;

    const failResult = {
      valid: false as const,
      encryptedPrivateKey: null,
      salt: null,
      recoveryEncryptedPrivateKey: null,
      recoverySalt: null,
      matchedCodeIndex: null,
    };

    const row = await db.query.userEncryptionKeys.findFirst({
      where: { userId },
      columns: {
        recoveryCodeHashes: true,
        recoveryEncryptedPrivateKeys: true,
        recoverySalt: true,
        encryptedPrivateKey: true,
        salt: true,
        lockedUntil: true,
        failedRecoveryAttempts: true,
      },
    });

    if (!row) return failResult;

    // Refuse if currently locked out
    if (row.lockedUntil && row.lockedUntil > new Date()) return failResult;

    // Find which stored hashes match the provided code hashes
    const matchedIndices: number[] = [];
    for (const codeHash of data.codeHashes) {
      const idx = row.recoveryCodeHashes.indexOf(codeHash);
      if (idx !== -1) matchedIndices.push(idx);
    }

    if (matchedIndices.length < 2) {
      const newCount = (row.failedRecoveryAttempts ?? 0) + 1;
      const lockout =
        newCount >= MAX_ATTEMPTS
          ? new Date(Date.now() + LOCKOUT_MINUTES * 60 * 1000)
          : undefined;

      await db
        .update(userEncryptionKeys)
        .set({
          failedRecoveryAttempts: newCount,
          ...(lockout ? { lockedUntil: lockout } : {}),
        })
        .where(eq(userEncryptionKeys.userId, userId));

      return failResult;
    }

    // Success — reset counter, invalidate used codes
    const remainingHashes = row.recoveryCodeHashes.filter(
      (_, i) => !matchedIndices.includes(i),
    );
    const remainingWrappedKeys = row.recoveryEncryptedPrivateKeys?.filter(
      (_, i) => !matchedIndices.includes(i),
    );

    // Return the recovery-wrapped key for the first matched code (if available)
    const firstMatchIdx = matchedIndices[0]!;
    const recoveryWrappedKey =
      row.recoveryEncryptedPrivateKeys?.[firstMatchIdx] ?? null;

    await db
      .update(userEncryptionKeys)
      .set({
        failedRecoveryAttempts: 0,
        recoveryCodeHashes: remainingHashes,
        recoveryEncryptedPrivateKeys: remainingWrappedKeys,
      })
      .where(eq(userEncryptionKeys.userId, userId));

    return {
      valid: true as const,
      encryptedPrivateKey: row.encryptedPrivateKey,
      salt: row.salt,
      recoveryEncryptedPrivateKey: recoveryWrappedKey,
      recoverySalt: row.recoverySalt ?? null,
      matchedCodeIndex: firstMatchIdx,
    };
  });

/** Reset PIN: update wrapped private key, salt, and optionally recovery codes. */
export const resetUserPinFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(ResetPinSchema)
  .handler(async ({ data, context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { userEncryptionKeys } = await import("drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const userId = context.user.id;

    await db
      .update(userEncryptionKeys)
      .set({
        encryptedPrivateKey: data.encryptedPrivateKey,
        salt: data.salt,
        failedPinAttempts: 0,
        lockedUntil: null,
        ...(data.recoveryCodeHashes
          ? { recoveryCodeHashes: data.recoveryCodeHashes }
          : {}),
      })
      .where(eq(userEncryptionKeys.userId, userId));

    return { success: true };
  });

/** Increment failed PIN attempts. Returns whether account is now locked. */
export const incrementFailedPinAttemptsFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { userEncryptionKeys } = await import("drizzle/schema");
    const { eq, sql } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const userId = context.user.id;

    const { MAX_ATTEMPTS, LOCKOUT_MINUTES } =
      await import("~/lib/crypto/constants");

    const [updated] = await db
      .update(userEncryptionKeys)
      .set({
        failedPinAttempts: sql`${userEncryptionKeys.failedPinAttempts} + 1`,
      })
      .where(eq(userEncryptionKeys.userId, userId))
      .returning({ failedPinAttempts: userEncryptionKeys.failedPinAttempts });

    if (!updated) {
      return { locked: false, attemptsRemaining: MAX_ATTEMPTS };
    }

    const attempts = updated.failedPinAttempts ?? 0;

    if (attempts >= MAX_ATTEMPTS) {
      const lockUntil = new Date(Date.now() + LOCKOUT_MINUTES * 60 * 1000);
      await db
        .update(userEncryptionKeys)
        .set({ lockedUntil: lockUntil })
        .where(eq(userEncryptionKeys.userId, userId));

      return { locked: true, attemptsRemaining: 0 };
    }

    return { locked: false, attemptsRemaining: MAX_ATTEMPTS - attempts };
  });

/** Reset failed PIN attempts on successful unlock. */
export const resetFailedPinAttemptsFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { userEncryptionKeys } = await import("drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();
    const userId = context.user.id;

    await db
      .update(userEncryptionKeys)
      .set({ failedPinAttempts: 0, lockedUntil: null })
      .where(eq(userEncryptionKeys.userId, userId));
  });
